import moment from 'moment'
import { CsvColumnConfig } from '../types/csv.types'
import { isNullLike } from './nullDetector'
import { normalizeDateFormatForParsing } from './dateFormatDetector'

const BOOLEAN_TRUE = new Set(['true', 'yes', 'y', 't', '1'])
const BOOLEAN_FALSE = new Set(['false', 'no', 'n', 'f', '0'])
const SAFE_INTEGER_DIGITS = 15

export interface ConversionResult {
    ok: boolean
    // Canonical string representation suitable for CSV output. `null` when the
    // input is null-like OR the value could not be converted (see `ok`).
    value: string | null
    // The raw input that caused the failure, useful for surfacing invalid examples.
    invalidSample?: string
}

function stripSign(value: string): { sign: string; rest: string } {
    if (value.startsWith('-')) return { sign: '-', rest: value.slice(1) }
    if (value.startsWith('+')) return { sign: '', rest: value.slice(1) }
    return { sign: '', rest: value }
}

function normalizeNumericString(raw: string, config: CsvColumnConfig): string | null {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const { sign, rest } = stripSign(trimmed)
    let v = rest
    if (config.thousandsSeparator) {
        const escaped = config.thousandsSeparator === ' ' ? ' ' : `\\${config.thousandsSeparator}`
        v = v.replace(new RegExp(escaped, 'g'), '')
    }
    if (config.decimalSeparator && config.decimalSeparator !== '.') {
        v = v.replace(config.decimalSeparator, '.')
    }
    if (!/^\d+(\.\d+)?$/.test(v)) return null
    return sign + v
}

function convertInteger(raw: string, config: CsvColumnConfig): ConversionResult {
    const normalized = normalizeNumericString(raw, config)
    if (normalized === null) return { ok: false, value: null, invalidSample: raw }
    if (normalized.includes('.')) return { ok: false, value: null, invalidSample: raw }
    const digits = normalized.replace(/^-/, '')
    if (digits.length > SAFE_INTEGER_DIGITS) {
        // Preserve as string to avoid precision loss; caller can still emit it.
        return { ok: true, value: normalized }
    }
    return { ok: true, value: normalized }
}

function convertFloat(raw: string, config: CsvColumnConfig): ConversionResult {
    const normalized = normalizeNumericString(raw, config)
    if (normalized === null) return { ok: false, value: null, invalidSample: raw }
    return { ok: true, value: normalized }
}

function convertBoolean(raw: string): ConversionResult {
    const lower = raw.trim().toLowerCase()
    if (BOOLEAN_TRUE.has(lower)) return { ok: true, value: 'true' }
    if (BOOLEAN_FALSE.has(lower)) return { ok: true, value: 'false' }
    return { ok: false, value: null, invalidSample: raw }
}

function convertDate(raw: string, config: CsvColumnConfig): ConversionResult {
    const v = raw.trim()
    if (!v) return { ok: false, value: null, invalidSample: raw }
    if (!config.dateFormat) return { ok: false, value: null, invalidSample: raw }
    const parsed = moment(v, normalizeDateFormatForParsing(config.dateFormat), true)
    if (!parsed.isValid()) return { ok: false, value: null, invalidSample: raw }
    return { ok: true, value: parsed.format('YYYY-MM-DD') }
}

function convertDatetime(raw: string, config: CsvColumnConfig): ConversionResult {
    const v = raw.trim()
    if (!v) return { ok: false, value: null, invalidSample: raw }
    if (!config.dateFormat) return { ok: false, value: null, invalidSample: raw }
    const parsed = moment(v, normalizeDateFormatForParsing(config.dateFormat), true)
    if (!parsed.isValid()) return { ok: false, value: null, invalidSample: raw }
    return { ok: true, value: parsed.format('YYYY-MM-DDTHH:mm:ss') }
}

export function convertValue(raw: string | null | undefined, config: CsvColumnConfig): ConversionResult {
    if (isNullLike(raw)) return { ok: true, value: null }
    const v = String(raw)
    switch (config.selectedType) {
        case 'integer':
            return convertInteger(v, config)
        case 'float':
            return convertFloat(v, config)
        case 'boolean':
            return convertBoolean(v)
        case 'date':
            return convertDate(v, config)
        case 'datetime':
            return convertDatetime(v, config)
        default:
            return { ok: true, value: v }
    }
}
